/**
 * PartnerDetail Page
 *
 * Displays complete information for a single partner configuration.
 * Desktop: Clean sectioned layout with unified appearance
 * Mobile: Card-based layout for better scrolling
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { logger } from '@/app/utils/logger';
import { PartnerTypeBadge, PartnerStatusBadge } from '@/components/common/Badge';
import { DefaultOffersEditor } from '@/components/partners/DefaultOffersEditor';
import { BulkAssignOfferModal } from '@/components/partners/BulkAssignOfferModal';
import {
  PartnerResponse,
  BulkAssignOfferResponse,
  BulkRemoveOfferResponse,
  BulkReplaceOfferResponse,
  formatDate,
  formatScheduledActivation,
} from '@/app/types/partners';
import { partnerService } from '@/app/services/partnerService';
import { auditService } from '@/app/services';

import './PartnerDetail.scss';

export const PartnerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [partner, setPartner] = useState<PartnerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const partnerData = await partnerService.getPartnerById(parseInt(id, 10));
        if (!partnerData) {
          setError('Partner not found');
          return;
        }

        setPartner(partnerData);

        // Log record view for audit
        auditService
          .logRecordView('partner', id, {
            page_url: `/partners/${id}`,
          })
          .catch((err) => logger.warn('Failed to log record view', { error: err }));
      } catch (err) {
        setError('Failed to load partner');
        logger.error('Failed to load partner', { error: err, partnerId: id });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleDeactivate = async () => {
    if (!id || !partner) return;

    if (!window.confirm(`Are you sure you want to deactivate "${partner.doing_business_as}"?`)) {
      return;
    }

    setToggling(true);
    try {
      const updated = await partnerService.deactivatePartner(parseInt(id, 10));
      if (updated) {
        // Log deactivation for audit
        auditService
          .logRecordModification('updated', 'partner', id, {
            action: 'deactivated',
            partner_name: partner?.partner_name,
          })
          .catch((err) => logger.warn('Failed to log deactivation', { error: err }));

        setPartner(updated);
      }
    } catch (err) {
      logger.error('Failed to deactivate partner', { error: err, partnerId: id });
      setError('Failed to deactivate partner');
    } finally {
      setToggling(false);
    }
  };

  const handleActivate = async () => {
    if (!id || !partner) return;

    setToggling(true);
    try {
      const updated = await partnerService.activatePartner(parseInt(id, 10));
      if (updated) {
        // Log activation for audit
        auditService
          .logRecordModification('updated', 'partner', id, {
            action: 'activated',
            partner_name: partner?.partner_name,
          })
          .catch((err) => logger.warn('Failed to log activation', { error: err }));

        setPartner(updated);
      }
    } catch (err) {
      logger.error('Failed to activate partner', { error: err, partnerId: id });
      setError('Failed to activate partner');
    } finally {
      setToggling(false);
    }
  };

  const handleBulkAssignSuccess = async (
    result: BulkAssignOfferResponse | BulkRemoveOfferResponse | BulkReplaceOfferResponse
  ) => {
    logger.info('Bulk operation completed', { result });
    // Refresh partner data to show updated default_offers
    if (result.partners_updated > 0 && id) {
      try {
        const updated = await partnerService.getPartnerById(parseInt(id, 10));
        if (updated) {
          setPartner(updated);
        }
      } catch (err) {
        logger.error('Failed to refresh partner after bulk assign', { error: err });
      }
    }
  };

  if (loading) {
    return (
      <div className="partner-detail-page">
        <div className="loading-state">
          <div className="spinner" />
          <span>Loading partner...</span>
        </div>
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div className="partner-detail-page">
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error || 'Partner not found'}</span>
          <button onClick={() => navigate('/partners')} className="btn-back">
            Back to Partners
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="partner-detail-page">
      {/* Back Navigation */}
      <div className="back-nav">
        <button onClick={() => navigate('/partners')} className="btn-back-link">
          ← Back to Partner Configurations
        </button>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="header-left">
          <div className="item-title">{partner.doing_business_as}</div>
          <p className="header-subtitle">{partner.partner_name}</p>
          <div className="header-badges">
            <PartnerStatusBadge
              isActive={partner.is_active}
              scheduledActivationAt={partner.scheduled_activation_at}
            />
            <PartnerTypeBadge type={partner.partner_type} />
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-bulk-assign" onClick={() => setBulkAssignModalOpen(true)}>
            📦 Bulk Manage Offers
          </button>
          <button className="btn-edit" onClick={() => navigate(`/partners/${id}/edit`)}>
            ✏️ Edit
          </button>
          {partner.is_active ? (
            <button className="btn-deactivate" onClick={handleDeactivate} disabled={toggling}>
              {toggling ? 'Processing...' : '⏸️ Deactivate'}
            </button>
          ) : (
            <button className="btn-activate" onClick={handleActivate} disabled={toggling}>
              {toggling ? 'Processing...' : '▶️ Activate'}
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="detail-container">
        {/* Basic Info Section */}
        <section className="detail-section">
          <h2 className="section-title">Basic Information</h2>
          <div className="info-grid two-col">
            <div className="info-group">
              <div className="info-row">
                <span className="info-label">Partner Product ID</span>
                <span className="info-value mono">{partner.partner_product_id}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Partner ID</span>
                <span className="info-value">{partner.partner_id ?? '—'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Partner Name</span>
                <span className="info-value">{partner.partner_name}</span>
              </div>
            </div>
            <div className="info-group">
              <div className="info-row">
                <span className="info-label">Doing Business As</span>
                <span className="info-value">{partner.doing_business_as}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Partner Type</span>
                <span className="info-value">
                  <PartnerTypeBadge type={partner.partner_type} />
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Program Code</span>
                <span className="info-value">{partner.program_code ?? '—'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Package ID</span>
                <span className="info-value">{partner.package_id ?? '—'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Purse Name</span>
                <span className="info-value">{partner.purse_name ?? '—'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Status</span>
                <span className="info-value">
                  <PartnerStatusBadge
                    isActive={partner.is_active}
                    scheduledActivationAt={partner.scheduled_activation_at}
                  />
                </span>
              </div>
              {!partner.is_active && partner.scheduled_activation_at && (
                <div className="info-row">
                  <span className="info-label">Scheduled Activation</span>
                  <span className="info-value">
                    {formatScheduledActivation(
                      partner.scheduled_activation_at,
                      partner.activation_timezone,
                    )}
                  </span>
                </div>
              )}
              <div className="info-row">
                <span className="info-label">Fulfillment</span>
                <span className="info-value">
                  {(partner.config?.fulfillment_enabled ?? true) ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Effective Period Section */}
        <section className="detail-section">
          <h2 className="section-title">Effective Period</h2>
          <div className="period-grid">
            <div className="period-item">
              <span className="period-label">Effective Date</span>
              <span className="period-value">{formatDate(partner.effective_date)}</span>
            </div>
            <div className="period-item">
              <span className="period-label">Expiration Date</span>
              <span className="period-value">
                {partner.expiration_date ? formatDate(partner.expiration_date) : 'None'}
              </span>
            </div>
          </div>
        </section>

        {/* Default Offers Section */}
        <section className="detail-section">
          <h2 className="section-title">Default Offers</h2>
          <p className="section-subtitle">Rewards automatically assigned to new users</p>
          <DefaultOffersEditor value={partner.default_offers} onChange={() => {}} readOnly />
        </section>

        {/* Earned Benefits Section */}
        <section className="detail-section">
          <h2 className="section-title">Earned Benefits</h2>
          {partner.earned_benefits.length > 0 ? (
            <div className="benefits-list">
              {partner.earned_benefits.map((benefit, idx) => (
                <span key={idx} className="benefit-tag">
                  {benefit}
                </span>
              ))}
            </div>
          ) : (
            <p className="no-content">No earned benefits configured</p>
          )}
        </section>

        {/* Notes Section */}
        {partner.notes && (
          <section className="detail-section">
            <h2 className="section-title">Notes</h2>
            <p className="notes-text">{partner.notes}</p>
          </section>
        )}

        {/* Metadata Section */}
        <section className="detail-section">
          <h2 className="section-title">Metadata</h2>
          <div className="timeline-grid">
            <div className="timeline-item">
              <span className="timeline-label">Created By</span>
              <span className="timeline-value">{partner.created_by}</span>
            </div>
            <div className="timeline-item muted">
              <span className="timeline-label">Created At</span>
              <span className="timeline-value">{formatDateTime(partner.created_at)}</span>
            </div>
            <div className="timeline-item muted">
              <span className="timeline-label">Updated At</span>
              <span className="timeline-value">{formatDateTime(partner.updated_at)}</span>
            </div>
          </div>
        </section>
      </div>

      {/* Bulk Assign Offer Modal */}
      <BulkAssignOfferModal
        isOpen={bulkAssignModalOpen}
        onClose={() => setBulkAssignModalOpen(false)}
        onSuccess={handleBulkAssignSuccess}
        partnerId={partner.partner_product_id}
        partnerName={partner.doing_business_as}
      />
    </div>
  );
};

export default PartnerDetail;
